import { AppError } from '@app/domain/entities';

/**
 * Thrown when a SQLite query fails.
 * Carries the SQL string and bound parameters so tests and AppErrorHandler
 * can inspect them without parsing console output.
 *
 * Note: construct on the **main thread** only — custom properties are stripped
 * when errors cross the OPFS worker postMessage boundary.
 */
export class SqliteQueryError extends AppError {
  readonly code = 'E_SQLITE_QUERY';

  constructor(
    public readonly sql: string,
    public readonly params: unknown[],
    message?: string,
    cause?: unknown,
  ) {
    super(message ?? 'SQLite query failed', { sql, params }, { cause });
  }
}

/**
 * Thrown when SQLite is misconfigured (e.g. OPFS unavailable or unsupported).
 * This is a fatal error that should never happen in a supported environment,
 * so it doesn't carry extra context beyond the message and cause.
 */
export class SqliteConfigurationError extends AppError {
  readonly code = 'E_SQLITE_CONFIG';

  constructor(message?: string, cause?: unknown) {
    super(message ?? 'SQLite configuration error', {}, { cause });
  }
}

/**
 * Thrown when an IndexedDB operation fails.
 * Carries the operation/store context plus recent connection lifecycle events.
 */
export class IndexedDbOperationError extends AppError {
  readonly code = 'E_INDEXEDDB_OPERATION';

  constructor(
    public readonly operation: string,
    context: Record<string, unknown>,
    cause?: unknown,
  ) {
    super(`IndexedDB operation failed during ${operation}`, { operation, ...context }, { cause });
  }
}
